import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TopNav } from '../components/TopNav';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';
import { EmptyState } from '../components/EmptyState';
import { CategoryPill } from '../components/CategoryPill';
import {
  getOutreachConfig,
  getOutreachInbox,
  patchLedgerEntry,
  patchReplyReview,
  type ReplyReviewPatch,
} from '../lib/api';
import {
  replyMatchesQueue,
  replyQueueCounts,
  replyQueueStatus,
  sortReplyQueue,
  type ReplyQueue,
} from '../lib/reply-queue';
import { REPLY_CATEGORIES, categoryLabel, categoryPillStyle } from '../lib/reply-taxonomy';
import { fmt } from '../lib/format';
import type {
  InboundReplyRow,
  LedgerEntry,
  ReplyLabelOverride,
  ReplyParseEngagement,
} from '../types';

type WarningFilter = 'needs_human' | 'unmatched' | null;
type EditorMode = 'follow_up' | 'dismiss' | 'note' | null;

const QUEUES: { id: ReplyQueue; label: string }[] = [
  { id: 'new', label: 'New' },
  { id: 'follow_up', label: 'Follow-up' },
  { id: 'done', label: 'Done' },
  { id: 'all', label: 'All' },
];

function isUnmatched(reply: InboundReplyRow): boolean {
  return reply.match_method === 'unmatched' || !reply.match_method;
}

function nextBusinessDate(): string {
  const date = new Date();
  do {
    date.setDate(date.getDate() + 1);
  } while (date.getDay() === 0 || date.getDay() === 6);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDateOnly(value: string): string {
  // PostgreSQL `date` values arrive as YYYY-MM-DD. Parsing that form directly
  // uses UTC and displays the prior day in North American time zones.
  return fmt.date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00` : value);
}

export function Inbox(): JSX.Element {
  const { data, isLoading } = useQuery({ queryKey: ['outreach-inbox'], queryFn: getOutreachInbox });
  const { data: runtimeConfig } = useQuery({ queryKey: ['outreach-config'], queryFn: getOutreachConfig });
  const replies = useMemo(() => data ?? [], [data]);
  const [queue, setQueue] = useState<ReplyQueue>('new');
  const [warningFilter, setWarningFilter] = useState<WarningFilter>(null);

  const counts = useMemo(() => replyQueueCounts(replies), [replies]);
  const warningCounts = useMemo(() => ({
    needsHuman: replies.filter((reply) => reply.parse?.needs_human).length,
    unmatched: replies.filter(isUnmatched).length,
  }), [replies]);

  const visible = useMemo(() => {
    const filtered = replies.filter((reply) => {
      if (!replyMatchesQueue(reply, queue)) return false;
      if (warningFilter === 'needs_human') return !!reply.parse?.needs_human;
      if (warningFilter === 'unmatched') return isUnmatched(reply);
      return true;
    });
    return sortReplyQueue(filtered, queue);
  }, [queue, replies, warningFilter]);

  return (
    <div className="ari-app">
      <TopNav />
      <div className="page">
        <div className="page-hd reply-page-hd">
          <div>
            <div className="eb">Coach outreach</div>
            <h1 className="d-title">Reply queue</h1>
            <div className="d-meta reply-page-intro">
              Triage each facilitator reply once: acknowledge it, assign follow-up, or dismiss it.
              Anthropic's read stays visible and correctable.
            </div>
          </div>
        </div>

        {runtimeConfig?.deliveryMode === 'test' && (
          <div className="reply-test-banner">
            <Icon name="alert" size={15} />
            <div>
              <strong>Test queue</strong>
              <span>
                Actions and notes are saved so you can test the full workflow. They do not change live
                drafts, suppressions, or coach context.
              </span>
            </div>
          </div>
        )}

        <div className="reply-queue-bar">
          <div className="reply-queue-tabs" role="tablist" aria-label="Reply queue">
            {QUEUES.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={queue === item.id}
                className={queue === item.id ? 'on' : ''}
                onClick={() => setQueue(item.id)}
              >
                <span>{item.label}</span>
                <span className="reply-tab-count">{counts[item.id]}</span>
              </button>
            ))}
          </div>

          <div className="reply-warning-filters" aria-label="Reply warning filters">
            <button
              type="button"
              className={warningFilter === 'needs_human' ? 'on' : ''}
              onClick={() => setWarningFilter((current) => current === 'needs_human' ? null : 'needs_human')}
            >
              <Icon name="alert" size={13} />
              Needs human <span>{warningCounts.needsHuman}</span>
            </button>
            <button
              type="button"
              className={warningFilter === 'unmatched' ? 'on' : ''}
              onClick={() => setWarningFilter((current) => current === 'unmatched' ? null : 'unmatched')}
            >
              <Icon name="link" size={13} />
              Unmatched <span>{warningCounts.unmatched}</span>
            </button>
          </div>
        </div>

        <div className="reply-list-wrap">
          {isLoading ? (
            <div className="d-meta" style={{ padding: 24 }}>Loading replies…</div>
          ) : visible.length === 0 ? (
            <EmptyState
              icon={queue === 'done' ? 'check' : 'user'}
              title={warningFilter ? 'No replies match this filter' : `No ${queue === 'all' ? '' : `${queue.replace('_', ' ')} `}replies`}
              body={
                warningFilter
                  ? 'Clear the warning filter or choose another queue.'
                  : queue === 'new'
                    ? 'You are at inbox zero. New facilitator replies will appear here.'
                    : queue === 'follow_up'
                      ? 'Nothing is waiting for a follow-up.'
                      : 'Replies appear here after they are acknowledged or dismissed.'
              }
              cta={warningFilter ? (
                <Button size="sm" variant="secondary" onClick={() => setWarningFilter(null)}>
                  Clear filter
                </Button>
              ) : undefined}
            />
          ) : (
            <div className="reply-list">
              {visible.map((reply) => <ReplyCard key={reply.id} reply={reply} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ReplyCard({ reply }: { reply: InboundReplyRow }): JSX.Element {
  const qc = useQueryClient();
  const parse = reply.parse;
  const queueStatus = replyQueueStatus(reply);
  const [editor, setEditor] = useState<EditorMode>(null);
  const [note, setNote] = useState(reply.review?.note ?? '');
  const [dueDate, setDueDate] = useState(reply.review?.follow_up_due_at ?? nextBusinessDate());

  useEffect(() => {
    setNote(reply.review?.note ?? '');
    setDueDate(reply.review?.follow_up_due_at ?? nextBusinessDate());
  }, [reply.review?.follow_up_due_at, reply.review?.note]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['outreach-inbox'] });
    qc.invalidateQueries({ queryKey: ['coach-ledger'] });
  };

  const review = useMutation({
    mutationFn: (patch: ReplyReviewPatch) => patchReplyReview(reply.id, patch),
    onSuccess: () => {
      invalidate();
      setEditor(null);
    },
  });

  const saveLabelOverride = async (engagementIndex: number, categories: string[]) => {
    if (!parse) return;
    const key = `${parse.id}:${engagementIndex}`;
    const existing = reply.review?.label_overrides ?? [];
    const labelOverrides: ReplyLabelOverride[] = [
      ...existing.filter((override) => `${override.parseId}:${override.engagementIndex}` !== key),
      { parseId: parse.id, engagementIndex, categories },
    ];
    await patchReplyReview(reply.id, { labelOverrides });
    invalidate();
  };

  const busy = review.isPending;
  const statusLabel = queueStatus === 'follow_up'
    ? 'Follow-up'
    : queueStatus === 'acknowledged'
      ? 'Acknowledged'
      : queueStatus === 'dismissed'
        ? 'Dismissed'
        : 'New';
  const statusClass = queueStatus === 'follow_up'
    ? 'follow-up'
    : queueStatus === 'acknowledged'
      ? 'acknowledged'
      : queueStatus === 'dismissed'
        ? 'dismissed'
        : 'new';

  const openEditor = (mode: Exclude<EditorMode, null>) => {
    setNote(reply.review?.note ?? '');
    setDueDate(reply.review?.follow_up_due_at ?? nextBusinessDate());
    setEditor(mode);
  };

  return (
    <article className={`reply-card reply-card-${statusClass}`}>
      <div className="reply-card-head">
        <div className="reply-sender">
          <div>
            <strong>{reply.from_name ?? reply.from_email ?? 'Unknown sender'}</strong>
            {reply.from_email && <span>{reply.from_email}</span>}
          </div>
          <div className="reply-subject">{reply.subject ?? '(No subject)'}</div>
        </div>

        <div className="reply-card-badges">
          <span className={`pill reply-status-pill ${statusClass}`}>
            <span className="pdot" />{statusLabel}
          </span>
          {reply.delivery_mode === 'test' && (
            <span
              className="pill reply-test-pill"
              title={`Delivered only to ${reply.delivered_to_email ?? 'the test recipient'}; intended for ${reply.intended_to_email ?? 'a facilitator'}`}
            >
              <span className="pdot" />Test reply
            </span>
          )}
          {parse?.needs_human && (
            <span className="pill reply-warn-pill"><span className="pdot" />Needs human</span>
          )}
          <span className="d-meta reply-match">
            <Icon name="link" size={11} />
            {reply.match_method ?? 'unmatched'}
            {reply.match_confidence != null ? ` · ${Math.round(reply.match_confidence * 100)}%` : ''}
          </span>
          <span className="d-tnum reply-time">{fmt.dateTime(reply.created_at)}</span>
        </div>
      </div>

      {parse ? (
        <div className="reply-ai-read">
          <div className="reply-ai-label">
            Anthropic read
            <span>{Math.round(parse.confidence * 100)}% confidence</span>
          </div>
          <div className="reply-ai-summary">{parse.summary}</div>
        </div>
      ) : (
        <div className="reply-ai-read pending">
          <div className="reply-ai-label">Parsing status</div>
          <div className="reply-ai-summary">
            {reply.status === 'correlated' ? 'Captured — Anthropic parsing has not completed yet.' : `Status: ${reply.status}`}
          </div>
        </div>
      )}

      {reply.ledger.length > 0 ? (
        <div className="reply-engagements">
          {reply.ledger.map((entry) => <LedgerReviewRow key={entry.id} entry={entry} />)}
        </div>
      ) : parse?.per_engagement.length ? (
        <div className="reply-engagements">
          {parse.per_engagement.map((engagement, index) => {
            const override = reply.review?.label_overrides.find(
              (item) => item.parseId === parse.id && item.engagementIndex === index,
            );
            return (
              <ParsePreviewLine
                key={`${parse.id}:${index}`}
                engagement={engagement}
                categories={override?.categories ?? engagement.categories}
                corrected={!!override}
                isTest={reply.delivery_mode === 'test'}
                onSave={(categories) => saveLabelOverride(index, categories)}
              />
            );
          })}
        </div>
      ) : null}

      {reply.review?.note && editor === null && (
        <div className="reply-note">
          <Icon name="user" size={13} />
          <div>
            <strong>Internal note</strong>
            <span>{reply.review.note}</span>
          </div>
        </div>
      )}

      {queueStatus === 'follow_up' && (
        <div className="reply-follow-up-meta">
          <Icon name="calendar" size={13} />
          <span>
            {reply.review?.follow_up_owner_email ?? 'Unassigned'}
            {reply.review?.follow_up_due_at ? ` · due ${formatDateOnly(reply.review.follow_up_due_at)}` : ' · no due date'}
          </span>
        </div>
      )}

      {reply.stripped_text && (
        <details className="reply-body-details">
          <summary>View complete reply</summary>
          <div>{reply.stripped_text}</div>
        </details>
      )}

      {reply.delivery_mode === 'test' && (
        <div className="reply-delivery-note">
          Delivered to {reply.delivered_to_email ?? '—'} for testing · intended facilitator{' '}
          {reply.intended_to_email ?? '—'} · no live outreach changes
        </div>
      )}

      {editor && (
        <ReplyActionEditor
          mode={editor}
          note={note}
          dueDate={dueDate}
          busy={busy}
          onNoteChange={setNote}
          onDueDateChange={setDueDate}
          onCancel={() => setEditor(null)}
          onSave={() => {
            if (editor === 'follow_up') {
              review.mutate({
                queueStatus: 'follow_up',
                note: note.trim() || null,
                followUpDueAt: dueDate || null,
              });
            } else if (editor === 'dismiss') {
              review.mutate({ queueStatus: 'dismissed', note: note.trim() || null });
            } else {
              review.mutate({ note: note.trim() || null });
            }
          }}
        />
      )}

      <div className="reply-card-actions">
        {queueStatus === 'new' && (
          <>
            <Button
              size="sm"
              variant="primary"
              icon="check"
              disabled={busy}
              onClick={() => review.mutate({ queueStatus: 'acknowledged' })}
            >
              {busy ? 'Saving…' : 'Acknowledge'}
            </Button>
            <Button size="sm" variant="secondary" icon="arrowRight" disabled={busy} onClick={() => openEditor('follow_up')}>
              Follow up
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => openEditor('note')}>
              {reply.review?.note ? 'Edit note' : 'Add note'}
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => openEditor('dismiss')}>
              Dismiss
            </Button>
          </>
        )}

        {queueStatus === 'follow_up' && (
          <>
            <Button
              size="sm"
              variant="primary"
              icon="check"
              disabled={busy}
              onClick={() => review.mutate({ queueStatus: 'acknowledged' })}
            >
              {busy ? 'Saving…' : 'Mark done'}
            </Button>
            <Button size="sm" variant="secondary" icon="calendar" disabled={busy} onClick={() => openEditor('follow_up')}>
              Edit follow-up
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => review.mutate({ queueStatus: 'new' })}>
              Return to New
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => openEditor('dismiss')}>
              Dismiss
            </Button>
          </>
        )}

        {(queueStatus === 'acknowledged' || queueStatus === 'dismissed') && (
          <>
            <Button size="sm" variant="secondary" icon="refresh" disabled={busy} onClick={() => review.mutate({ queueStatus: 'new' })}>
              Reopen
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => openEditor('note')}>
              {reply.review?.note ? 'Edit note' : 'Add note'}
            </Button>
            <span className="reply-reviewed-stamp">
              {queueStatus === 'acknowledged' ? 'Acknowledged' : 'Dismissed'}
              {reply.review?.reviewed_by_email ? ` by ${reply.review.reviewed_by_email}` : ''}
              {reply.review?.reviewed_at ? ` · ${fmt.dateTime(reply.review.reviewed_at)}` : ''}
            </span>
          </>
        )}

        {review.isError && <span className="reply-action-error">Could not save. Please try again.</span>}
      </div>
    </article>
  );
}

interface ReplyActionEditorProps {
  mode: Exclude<EditorMode, null>;
  note: string;
  dueDate: string;
  busy: boolean;
  onNoteChange(value: string): void;
  onDueDateChange(value: string): void;
  onCancel(): void;
  onSave(): void;
}

function ReplyActionEditor({
  mode,
  note,
  dueDate,
  busy,
  onNoteChange,
  onDueDateChange,
  onCancel,
  onSave,
}: ReplyActionEditorProps): JSX.Element {
  const title = mode === 'follow_up'
    ? 'Add to the follow-up queue'
    : mode === 'dismiss'
      ? 'Dismiss this reply'
      : 'Internal note';
  const help = mode === 'follow_up'
    ? 'Capture what should happen next. You will be assigned automatically.'
    : mode === 'dismiss'
      ? 'Optionally record why this reply is not actionable.'
      : 'Notes are internal and never included in facilitator emails.';

  return (
    <div className="reply-action-editor">
      <div className="reply-action-editor-title">{title}</div>
      <div className="d-meta">{help}</div>
      <div className={mode === 'follow_up' ? 'reply-action-fields with-date' : 'reply-action-fields'}>
        <label>
          <span className="input-label">Note {mode === 'follow_up' ? '(recommended)' : '(optional)'}</span>
          <textarea
            value={note}
            maxLength={2000}
            rows={3}
            placeholder={
              mode === 'follow_up'
                ? 'e.g. Ask which engagement and when it will be updated'
                : 'Add context for the team'
            }
            onChange={(event) => onNoteChange(event.target.value)}
          />
        </label>
        {mode === 'follow_up' && (
          <label>
            <span className="input-label">Due date</span>
            <input type="date" className="input" value={dueDate} onChange={(event) => onDueDateChange(event.target.value)} />
          </label>
        )}
      </div>
      <div className="reply-action-editor-buttons">
        <Button
          size="sm"
          variant={mode === 'dismiss' ? 'secondary' : 'primary'}
          icon={mode === 'follow_up' ? 'arrowRight' : mode === 'dismiss' ? 'x' : 'check'}
          disabled={busy}
          onClick={onSave}
        >
          {busy ? 'Saving…' : mode === 'follow_up' ? 'Save follow-up' : mode === 'dismiss' ? 'Dismiss reply' : 'Save note'}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

function LedgerReviewRow({ entry }: { entry: LedgerEntry }): JSX.Element {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string[]>(entry.categories);

  useEffect(() => {
    setDraft(entry.categories);
  }, [entry.categories]);

  const saveEdit = useMutation({
    mutationFn: () => patchLedgerEntry(entry.id, { reviewStatus: 'edited', categories: draft }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['outreach-inbox'] });
      qc.invalidateQueries({ queryKey: ['coach-ledger'] });
      setEditing(false);
    },
  });

  const who = [entry.coachee_name, entry.account_name].filter(Boolean).join(' · ') || 'Engagement';

  return (
    <div className="reply-engagement-row">
      <div className="reply-engagement-line">
        <span className="reply-engagement-name">{who}</span>
        {entry.categories.map((category) => <CategoryPill key={category} category={category} />)}
        {entry.promised_log_date && (
          <span className="d-meta reply-promised-date">
            <Icon name="calendar" size={11} />by {formatDateOnly(entry.promised_log_date)}
          </span>
        )}
        <span className={`reply-label-state ${entry.review_status}`}>
          {entry.review_status === 'unverified'
            ? 'Confirm with Acknowledge'
            : entry.review_status === 'edited'
              ? 'Corrected'
              : 'Confirmed'}
        </span>
        {!editing && (
          <Button size="sm" variant="ghost" disabled={saveEdit.isPending} onClick={() => setEditing(true)}>
            Correct labels
          </Button>
        )}
      </div>

      {editing && (
        <CategoryEditor
          categories={draft}
          busy={saveEdit.isPending}
          onChange={setDraft}
          onSave={() => saveEdit.mutate()}
          onCancel={() => setEditing(false)}
        />
      )}
    </div>
  );
}

interface ParsePreviewLineProps {
  engagement: ReplyParseEngagement;
  categories: string[];
  corrected: boolean;
  isTest: boolean;
  onSave(categories: string[]): Promise<void>;
}

function ParsePreviewLine({
  engagement,
  categories,
  corrected,
  isTest,
  onSave,
}: ParsePreviewLineProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(categories);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const who = [engagement.matched_coachee_name, engagement.matched_account_name].filter(Boolean).join(' · ')
    || [...engagement.mentioned_coachee_names, ...engagement.mentioned_client_names].filter(Boolean).join(' · ')
    || 'Engagement';
  const matched = !!engagement.matched_opportunity_id;

  useEffect(() => setDraft(categories), [categories]);

  const save = async () => {
    setSaving(true);
    setFailed(false);
    try {
      await onSave(draft);
      setEditing(false);
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="reply-engagement-row">
      <div className="reply-engagement-line">
        <span className="reply-engagement-name">{who}</span>
        {categories.map((category) => <CategoryPill key={category} category={category} />)}
        <span className="d-meta reply-preview-state">
          {matched
            ? `Matched${engagement.deterministic_match_confidence != null ? ` · ${Math.round(engagement.deterministic_match_confidence * 100)}%` : ''}`
            : 'Not matched'}
          {isTest ? ' · test-safe preview' : ''}
          {corrected ? ' · corrected' : ''}
        </span>
        {!editing && (
          <Button size="sm" variant="ghost" disabled={saving} onClick={() => setEditing(true)}>
            Correct labels
          </Button>
        )}
      </div>

      {editing && (
        <CategoryEditor
          categories={draft}
          busy={saving}
          error={failed ? 'Could not save the correction.' : undefined}
          onChange={setDraft}
          onSave={save}
          onCancel={() => setEditing(false)}
        />
      )}
    </div>
  );
}

interface CategoryEditorProps {
  categories: string[];
  busy: boolean;
  error?: string;
  onChange(categories: string[]): void;
  onSave(): void;
  onCancel(): void;
}

function CategoryEditor({
  categories,
  busy,
  error,
  onChange,
  onSave,
  onCancel,
}: CategoryEditorProps): JSX.Element {
  const toggle = (category: string) => {
    onChange(categories.includes(category)
      ? categories.filter((value) => value !== category)
      : [...categories, category]);
  };

  return (
    <div className="reply-category-editor">
      <div className="d-meta">Choose every label that applies:</div>
      <div className="reply-category-options">
        {REPLY_CATEGORIES.map((category) => {
          const selected = categories.includes(category);
          const style = categoryPillStyle(category);
          return (
            <button
              key={category}
              type="button"
              className={selected ? 'selected' : ''}
              style={selected ? { background: style.background, color: style.color } : undefined}
              onClick={() => toggle(category)}
            >
              {categoryLabel(category)}
            </button>
          );
        })}
      </div>
      {error && <div className="reply-action-error">{error}</div>}
      <div className="reply-category-buttons">
        <Button size="sm" variant="primary" icon="check" disabled={busy || categories.length === 0} onClick={onSave}>
          {busy ? 'Saving…' : 'Save labels'}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}
